/**
 * Central registry for in-app tutorial hints.
 * Each entry maps a stable ID to the user-facing hint copy that renders
 * inside <TutorialHint id="..." />. Keep IDs descriptive and namespaced
 * (e.g. "sidebar.leads") so they read well at the call site.
 */

export interface TutorialHintEntry {
  title: string;
  body: string;
}

export const TUTORIAL_HINTS: Record<string, TutorialHintEntry> = {
  "sidebar.leads": {
    title: "Leads",
    body:
      "Leads are people you haven't booked yet. Add prospects here, log calls and emails, then mark them Booked when an event is confirmed.",
  },
  "sidebar.clients": {
    title: "Clients",
    body:
      "Clients are venues and contacts you've booked at least once. They're created automatically when you log an event from a lead.",
  },
  "sidebar.forms": {
    title: "Booking Forms",
    body:
      "Public booking pages your prospects can fill out to request an event. Each form is tied to an talent and (optionally) a client type.",
  },
  "sidebar.invoices": {
    title: "Invoices",
    body:
      "Every booked show creates an invoice. Track payment status, send reminders, and collect payment online.",
  },
  "sidebar.masterSchedule": {
    title: "Master Schedule",
    body:
      "A bird's-eye view of every booked show across every talent and region. Filter by date, status, or performer.",
  },
  "sidebar.emailHub": {
    title: "Email Hub",
    body:
      "Connect Gmail to send and receive booking emails right inside the app. Replies are matched back to the lead or client automatically.",
  },
  "logShow.button": {
    title: "Log an Event",
    body:
      "Use this when a booking is confirmed. It creates the client (if new), books the date, and generates an invoice in one step.",
  },
  "lead.convert": {
    title: "Mark as Booked",
    body:
      "Marking a lead as Booked moves it into your Clients list and starts the invoice flow. Use 'Log an Event' if you want to enter the event details right away.",
  },
  "invoice.create": {
    title: "Log an Event / Create Invoice",
    body:
      "All invoices are created by logging an event. Pick a client (or type a new one), enter date and rate, and the invoice is generated for you.",
  },
  "admin.users": {
    title: "Users & Permissions",
    body:
      "Add team members, set their role (Sales Rep, Admin, Billing, etc.), and control which client types and regions they can see.",
  },
  "admin.talent": {
    title: "Talent",
    body:
      "Each talent is a performer or act you book on behalf of. You'll need at least one before you can take bookings.",
  },
  "admin.clientTypes": {
    title: "Client Types",
    body:
      "Categorize your clients (e.g. Senior Living, Corporate, Festival). Pricing, forms, and reports can all be filtered by type.",
  },
  "admin.regionAccess": {
    title: "Region Settings",
    body:
      "Group clients geographically. Assigning regions to sales reps limits what they can see — useful when reps own a territory.",
  },
  "profile.tutorialMode": {
    title: "Tutorial Hints",
    body:
      "When on, the small `?` icons appear next to features throughout the app. Turn it off once you're comfortable to hide them.",
  },
};

export function getTutorialHint(id: string): TutorialHintEntry | undefined {
  return TUTORIAL_HINTS[id];
}
